import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { message } from "antd";
import {
  createBusinessUnit,
  deleteBusinessUnit,
  getBusinessUnitLists,
  updateBusinessUnitStatus,
  assignCompanyToBusinessUnit,
  getAssignCompanyList as fetchAssignCompanyList,
  getAssignUserList as fetchAssignUserList,
  getBusinessUnitById,
  getBusinessUnitUsers as fetchBusinessUnitUsers,
} from "./businessUnitAPI";

const initialState = {
  loading: false,
  deleteLoading: false,
  updateLoading: false,
  assignLoading: false,
  assignUserLoading: false,
  assignCompanyLoading: false,
  businessUnitList: [],
  pagination: {},
  assignCompanyList: [],
  assignUserList: [],
  businessUnitDetails: {},
  businessUnitUsers: [],
};

const actions = {
  GETBUSINESSUNITLIST: "businessUnit/GETBUSINESSUNITLIST",
  CREATEBUSINESSUNIT: "businessUnit/CREATEBUSINESSUNIT",
  DELETEBUSINESSUNIT: "businessUnit/DELETEBUSINESSUNIT",
  UPDATEBUSINESSUNITSTATUS: "businessUnit/UPDATEBUSINESSUNITSTATUS",
  ASSIGNCOMPANY: "businessUnit/ASSIGNCOMPANY",
  GETASSIGNCOMPANYLIST: "businessUnit/GETASSIGNCOMPANYLIST",
  GETASSIGNUSERLIST: "businessUnit/GETASSIGNUSERLIST",
  GETBUSINESSUNITDETAILS: "businessUnit/GETBUSINESSUNITDETAILS",
  GETBUSINESSUNITUSERS: "businessUnit/GETBUSINESSUNITUSERS",
};

export const getBusinessUnits = createAsyncThunk(
  actions.GETBUSINESSUNITLIST,
  async (payload) => {
    const response = await getBusinessUnitLists(
      payload?.page,
      payload?.per_page,
      payload?.search,
    );
    return response;
  },
);

export const addBusinessUnit = createAsyncThunk(
  actions.CREATEBUSINESSUNIT,
  async (payload) => {
    const response = await createBusinessUnit(payload);
    return response;
  },
);

export const removeBusinessUnit = createAsyncThunk(
  actions.DELETEBUSINESSUNIT,
  async (payload) => {
    const response = await deleteBusinessUnit(payload);
    return response;
  },
);

export const changeBusinessUnitStatus = createAsyncThunk(
  actions.UPDATEBUSINESSUNITSTATUS,
  async (payload) => {
    const response = await updateBusinessUnitStatus(payload);
    return response;
  },
);

export const assignCompany = createAsyncThunk(
  actions.ASSIGNCOMPANY,
  async (payload) => {
    const response = await assignCompanyToBusinessUnit(payload);
    return response;
  },
);

export const getAssignCompanyList = createAsyncThunk(
  actions.GETASSIGNCOMPANYLIST,
  async (payload) => {
    const response = await fetchAssignCompanyList(payload);
    return response;
  },
);

export const getAssignUserList = createAsyncThunk(
  actions.GETASSIGNUSERLIST,
  async (payload) => {
    const response = await fetchAssignUserList(payload);
    return response;
  },
);

export const getBusinessUnitDetails = createAsyncThunk(
  actions.GETBUSINESSUNITDETAILS,
  async (id) => {
    const response = await getBusinessUnitById(id);
    return response;
  },
);

export const getBusinessUnitUsers = createAsyncThunk(
  actions.GETBUSINESSUNITUSERS,
  async (payload) => {
    const response = await fetchBusinessUnitUsers(
      payload?.companyId,
      payload?.businessUnitId,
    );
    return response;
  },
);

export const businessUnitSlice = createSlice({
  name: "businessUnit",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getBusinessUnits.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBusinessUnits.fulfilled, (state, action) => {
        const { data, pagination, success, message: msg } = action.payload;
        state.loading = false;
        state.businessUnitList = data?.data;
        state.pagination = pagination;
        if (success === false) {
          message.error(msg);
        }
      })
      .addCase(getBusinessUnits.rejected, (state, action) => {
        const { message: msg } = action.error;
        state.loading = false;
        message.error(msg);
      });

    builder
      .addCase(addBusinessUnit.pending, (state) => {
        state.loading = true;
      })
      .addCase(addBusinessUnit.fulfilled, (state, action) => {
        const { success, message: msg } = action.payload;
        state.loading = false;
        if (success) {
          message.success(msg);
        } else {
          message.error(msg);
        }
      })
      .addCase(addBusinessUnit.rejected, (state, action) => {
        const { message: msg } = action.error;
        state.loading = false;
        message.error(msg);
      });

    builder
      .addCase(removeBusinessUnit.pending, (state) => {
        state.deleteLoading = true;
      })
      .addCase(removeBusinessUnit.fulfilled, (state, action) => {
        const { success, message: msg } = action.payload;
        state.deleteLoading = false;
        if (success) {
          message.success(msg);
        } else {
          message.error(msg);
        }
      })
      .addCase(removeBusinessUnit.rejected, (state, action) => {
        const { message: msg } = action.error;
        state.loading = false;
        message.error(msg);
      });

    builder
      .addCase(changeBusinessUnitStatus.pending, (state) => {
        state.updateLoading = true;
      })
      .addCase(changeBusinessUnitStatus.fulfilled, (state, action) => {
        const { success, message: msg } = action.payload;
        state.updateLoading = false;
        if (success) {
          message.success(msg);
        } else {
          message.error(msg);
        }
      })
      .addCase(changeBusinessUnitStatus.rejected, (state, action) => {
        const { message: msg } = action.error;
        state.loading = false;
        message.error(msg);
      });

    builder
      .addCase(assignCompany.pending, (state) => {
        state.assignLoading = true;
      })
      .addCase(assignCompany.fulfilled, (state, action) => {
        const { success, message: msg } = action.payload;
        state.assignLoading = false;
        if (success) {
          message.success(msg);
        } else {
          message.error(msg);
        }
      })
      .addCase(assignCompany.rejected, (state, action) => {
        const { message: msg } = action.error;
        state.assignLoading = false;
        message.error(msg);
      });

    builder
      .addCase(getAssignCompanyList.pending, (state) => {
        state.assignCompanyLoading = true;
      })
      .addCase(getAssignCompanyList.fulfilled, (state, action) => {
        const { data, pagination, success, message: msg } = action.payload;
        state.assignCompanyLoading = false;
        state.assignCompanyList = data;
        state.pagination = pagination;
        if (success === false) {
          message.error(msg);
        }
      })
      .addCase(getAssignCompanyList.rejected, (state, action) => {
        const { message: msg } = action.error;
        state.assignCompanyLoading = false;
        message.error(msg);
      });

    builder
      .addCase(getAssignUserList.pending, (state) => {
        state.assignUserLoading = true;
      })
      .addCase(getAssignUserList.fulfilled, (state, action) => {
        const { data, pagination, success, message: msg } = action.payload;
        state.assignUserLoading = false;
        state.assignUserList = data;
        state.pagination = pagination;
        if (success === false) {
          message.error(msg);
        }
      })
      .addCase(getAssignUserList.rejected, (state, action) => {
        const { message: msg } = action.error;
        state.assignUserLoading = false;
        message.error(msg);
      });

    builder
      .addCase(getBusinessUnitDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBusinessUnitDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.businessUnitDetails = action.payload?.data;
      })
      .addCase(getBusinessUnitDetails.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error.message);
      });

    builder
      .addCase(getBusinessUnitUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBusinessUnitUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.businessUnitUsers = action.payload?.data;
      })
      .addCase(getBusinessUnitUsers.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error.message);
      });
  },
});

export const businessUnitActions = businessUnitSlice.actions;
export default businessUnitSlice.reducer;
